// The InFront box's note (pipeline RC14; drawings c5-states.html "InFront's streams after 1 July" and a6-account-file.html
// "Also needs you"): for a client reading no calls since 1 July, "It went direct to InFront" notes who confirmed it,
// through track A's RC9 route, and Undo takes the note off; while a note stands the box says "Went direct" with the
// names and the day. "Ask <owner> to confirm it went direct" is the one yellow lead where the read names who handles
// InFront's record: a task on this company, put to them through Assign, as a task's own page puts one. Nothing here
// leaves Cobrain. The box shows only where the read carries RC9's field, so it never offers a route the build lacks.
import { ActionInput, ActionButton } from '../parts/index.js';
import { useState } from 'react';
import { records, type InFrontRouteAnswer } from '../api';
import { tasks } from '../tasks/api';
import { plateDay, type CompanyRead } from './company-read';
import type { CompanyReadings } from './readings';

/** "Confirmed by Mike Thomas; noted by Will Fyler on 7 Oct 2026." RC9 keeps no noter where the person has left. */
export function wentDirectWords(r: NonNullable<CompanyRead['inFrontRoute']>): string {
  return `Confirmed by ${r.confirmedBy}; noted ${r.notedBy ? `by ${r.notedBy} ` : ''}on ${plateDay(r.notedAt.slice(0, 10))}.`;
}

const said = (verb: string, e: unknown) => `${verb}: ${e instanceof Error ? e.message.replace(/\.$/, '') : String(e)}.`;

export function InFrontNote({ storeId, companyId, name, read, readings, mayEdit, onNoted }: {
  storeId: string; companyId: string; name: string; read: CompanyRead; readings: CompanyReadings | null; mayEdit: boolean;
  onNoted: (a: InFrontRouteAnswer) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [who, setWho] = useState('');
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  const [asked, setAsked] = useState<string | null>(null);
  if (read.inFrontRoute === undefined) return null;
  const route = read.inFrontRoute;
  if (route) {
    const undo = async () => {
      setBusy(true); setWrong(null);
      try { onNoted(await records.undoWentDirect(storeId, companyId)); } catch (e) { setWrong(said('Not undone', e)); } finally { setBusy(false); }
    };
    // Where the three boxes are drawn, the third already says it in these words; the note then carries only its Undo.
    const boxed = (readings?.inFront.length ?? 0) > 0;
    if (boxed && !mayEdit) return null;
    return (
      <div className="if-note" id="a-infront-note">
        {boxed ? null : <p><b>Went direct to InFront.</b> {wentDirectWords(route)} The record keeps the months before July and no longer measures InFront's streams for {name}.</p>}
        {mayEdit ? <ActionButton type="button" className="hx-link" disabled={busy} onClick={() => void undo()}>{busy ? 'Undoing' : boxed ? 'Undo the note that it went direct' : 'Undo'}</ActionButton> : null}
        {wrong ? <span className="awrong" role="alert">{wrong}</span> : null}
      </div>
    );
  }
  if (!mayEdit || !(readings?.inFront ?? []).some((r) => r.label === 'no_calls')) return null;
  const owner = read.inFrontRecord?.owner ?? null;
  const note = async () => {
    const confirmedBy = who.trim();
    if (!confirmedBy) { setWrong('Say who confirmed it went direct.'); return; }
    setBusy(true); setWrong(null);
    try { onNoted(await records.noteWentDirect(storeId, companyId, confirmedBy)); setAsking(false); setWho(''); } catch (e) { setWrong(said('Not noted', e)); } finally { setBusy(false); }
  };
  const ask = async () => {
    if (!owner) return;
    setBusy(true); setWrong(null);
    try {
      const due = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
      const { id } = await tasks.add(storeId, { title: `Confirm ${name} went direct to InFront`, dueAt: due, priority: 2, organisationId: companyId });
      await tasks.act(storeId, id, 'assign', { to: owner.principalId, message: `${name} has made no calls for InFront's streams since 1 July. Can you confirm it went direct to InFront?` });
      setAsked(`Put to ${owner.name}: a task on their list.`);
    } catch (e) { setWrong(said('Not asked', e)); } finally { setBusy(false); }
  };
  return (
    <div className="if-note" id="a-infront-note">
      {asking ? (
        <form className="if-form" onSubmit={(e) => { e.preventDefault(); void note(); }}>
          <label htmlFor="if-who">Who confirmed {name} went direct to InFront?</label>
          <ActionInput id="if-who" value={who} maxLength={200} autoComplete="off" placeholder="Their name" onChange={(e) => setWho(e.target.value)} />
          <span className="opts">
            <ActionButton type="submit" className="opt lead" disabled={busy}>{busy ? 'Noting it' : 'Note it'}</ActionButton>
            <ActionButton type="button" className="opt" disabled={busy} onClick={() => { setAsking(false); setWrong(null); }}>Cancel</ActionButton>
          </span>
        </form>
      ) : (
        <span className="opts">
          {owner && !asked ? (
            <ActionButton type="button" className="opt lead" disabled={busy} onClick={() => void ask()}>
              <span className="l">Ask {owner.name} to confirm it went direct</span><small>a task on their list</small>
            </ActionButton>
          ) : null}
          <ActionButton type="button" className="opt" disabled={busy} onClick={() => setAsking(true)}>It went direct to InFront</ActionButton>
        </span>
      )}
      {asked ? <p className="if-said" role="status">{asked}</p> : null}
      {wrong ? <span className="awrong" role="alert">{wrong}</span> : null}
    </div>
  );
}
